const API_BASE_URL = 'https://kritics-api.krisloertscher.workers.dev/api';

// Helper function for fetch requests
const fetchData = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Something went wrong');
  }

  return response.json();
};

const api = {
  // Auth
  register: (userData) => {
    return fetchData(`${API_BASE_URL}/users/register`, {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  },

  login: (credentials) => {
    return fetchData(`${API_BASE_URL}/users/login`, {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
  },

  // Admin
  adminListUsers: (adminId) => {
    return fetchData(`${API_BASE_URL}/users/admin/list`, {
      headers: { 'x-admin-id': adminId }
    });
  },
  adminInviteUser: (adminId, username) => {
    return fetchData(`${API_BASE_URL}/users/admin/invite`, {
      method: 'POST',
      headers: { 'x-admin-id': adminId },
      body: JSON.stringify({ username })
    });
  },
  adminToggleAccess: (adminId, targetUserId, allowed) => {
    return fetchData(`${API_BASE_URL}/users/admin/toggle-access`, {
      method: 'POST',
      headers: { 'x-admin-id': adminId },
      body: JSON.stringify({ targetUserId, allowed })
    });
  },

  adminDeleteUser: (adminId, targetUserId) => {
    return fetchData(`${API_BASE_URL}/users/admin/user/${targetUserId}`, {
      method: 'DELETE',
      headers: { 'x-admin-id': adminId }
    });
  },

  getSystemStatus: (adminId) => {
    return fetchData(`${API_BASE_URL}/users/admin/system-status`, {
      headers: { 'x-admin-id': adminId }
    });
  },

  adminUpdateSystemStatus: (adminId, key, value) => {
    return fetchData(`${API_BASE_URL}/users/admin/system-status/toggle`, {
      method: 'POST',
      headers: { 'x-admin-id': adminId },
      body: JSON.stringify({ key, value })
    });
  },

  adminTriggerScraper: (adminId, letter) => {
    return fetchData(`${API_BASE_URL}/users/admin/trigger-scraper`, {
      method: 'POST',
      headers: { 'x-admin-id': adminId },
      body: JSON.stringify({ letter })
    });
  },

  adminTriggerTMDBSync: (adminId, pages) => {
    return fetchData(`${API_BASE_URL}/users/admin/trigger-tmdb-sync`, {
      method: 'POST',
      headers: { 'x-admin-id': adminId },
      body: JSON.stringify({ pages })
    });
  },

  // Profile Resets
  resetAllData: (userId) => {
    return fetchData(`${API_BASE_URL}/users/${userId}/reset-all`, { method: 'POST' });
  },
  resetRatings: (userId) => {
    return fetchData(`${API_BASE_URL}/users/${userId}/reset-ratings`, { method: 'POST' });
  },
  resetWatchlist: (userId) => {
    return fetchData(`${API_BASE_URL}/users/${userId}/reset-watchlist`, { method: 'POST' });
  },
  resetDismissed: (userId) => {
    return fetchData(`${API_BASE_URL}/users/${userId}/reset-dismissed`, { method: 'POST' });
  },
  acceptBetaTerms: (userId) => {
    return fetchData(`${API_BASE_URL}/users/${userId}/accept-terms`, { method: 'POST' });
  },

  // Movies
  getPopularMovies: (page = 1, includeAdult = false, sortBy = 'popularity.desc') => {
    return fetchData(`${API_BASE_URL}/movies/popular?page=${page}&include_adult=${includeAdult}&sort_by=${sortBy}`);
  },

  searchMovies: (query, page = 1, includeAdult = false) => {
    return fetchData(`${API_BASE_URL}/movies/search?query=${encodeURIComponent(query)}&page=${page}&include_adult=${includeAdult}`);
  },

  getMovieDetails: (movieId) => {
    return fetchData(`${API_BASE_URL}/movies/${movieId}`);
  },

  getMovieDetailsBySlug: (slug, userId) => {
    return fetchData(`${API_BASE_URL}/movies/${slug}?userId=${userId}`);
  },

  // Ratings
  rateMovie: (userId, movieId, rating, idType = 'tmdb') => {
    return fetchData(`${API_BASE_URL}/users/rate`, {
      method: 'POST',
      body: JSON.stringify({ userId, movieId, rating, idType })
    });
  },

  getUserRatings: (userId) => {
    return fetchData(`${API_BASE_URL}/users/${userId}/ratings`);
  },

  dismissMovie: (userId, movieId, idType = 'tmdb') => {
    return fetchData(`${API_BASE_URL}/users/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ userId, movieId, idType })
    });
  },

  toggleWannaWatch: (userId, movieId, wannaWatch, idType = 'tmdb') => {
    return fetchData(`${API_BASE_URL}/users/wanna-watch`, {
      method: 'POST',
      body: JSON.stringify({ userId, movieId, wannaWatch, idType })
    });
  },

  getWannaWatchMovies: (userId) => {
    return fetchData(`${API_BASE_URL}/users/${userId}/wanna-watch`);
  },

  // Critics
  getKriticalWatching: (userId, page = 1, limit = 20) => {
    return fetchData(`${API_BASE_URL}/users/${userId}/kritical-watching?page=${page}&limit=${limit}`);
  },

  getNewKriticalMovies: (userId, page = 1, limit = 20) => {
    return fetchData(`${API_BASE_URL}/users/${userId}/new-kritical-movies?page=${page}&limit=${limit}`);
  },

  getMatchingKritics: (userId) => {
    return fetchData(`${API_BASE_URL}/users/${userId}/matching-kritics`);
  },

  getKriticDetails: (kriticId) => {
    return fetchData(`${API_BASE_URL}/kritics/${kriticId}`);
  },

  importKriticsFromTMDB: (movieId) => {
    return fetchData(`${API_BASE_URL}/kritics/import-from-tmdb`, {
      method: 'POST',
      body: JSON.stringify({ movieId })
    });
  },

  // Add this method to the api object
  fixKriticProfileUrls: () => {
    return fetchData(`${API_BASE_URL}/kritics/fix-profile-urls`, {
      method: 'POST'
    });
  },

  // Letterboxd Import
  uploadLetterboxdZip: async (file) => {
    const formData = new FormData();
    formData.append('zipfile', file);

    const response = await fetch(`${API_BASE_URL}/import/letterboxd/upload`, {
      method: 'POST',
      body: formData
    });

    const text = await response.text();
    console.log('Upload response status:', response.status);
    console.log('Upload response text:', text);

    if (!response.ok) {
      try {
        const error = JSON.parse(text);
        throw new Error(error.message || error.error || 'Failed to upload file');
      } catch (parseError) {
        throw new Error(`Server error: ${text.substring(0, 200)}`);
      }
    }

    try {
      return JSON.parse(text);
    } catch (parseError) {
      console.error('Failed to parse response:', text);
      throw new Error('Invalid response from server');
    }
  },

  matchLetterboxdMovies: (movies, userId, offset = 0, limit = 25) => {
    return fetchData(`${API_BASE_URL}/import/letterboxd/match`, {
      method: 'POST',
      body: JSON.stringify({ movies, userId, offset, limit })
    });
  },

  executeLetterboxdImport: (importData) => {
    return fetchData(`${API_BASE_URL}/import/letterboxd/execute`, {
      method: 'POST',
      body: JSON.stringify(importData)
    });
  },

  searchTMDB: (query, year) => {
    const yearParam = year ? `&year=${year}` : '';
    return fetchData(`${API_BASE_URL}/import/letterboxd/search-tmdb?query=${encodeURIComponent(query)}${yearParam}`);
  },
};

export default api;
